/**
 * +quests: little props next to the side-quest NPCs (render/toon/outdoor/questNpcLooks.ts): a
 * washing line with one lonely sock, a yarn basket, a jar of slime, an empty scarecrow post, a
 * turnip patch, a spade in a fresh mound, a fishing spot (rod and bucket), a soup pot, a suitcase,
 * an ore cart, a goat, a candle cluster, a "main bandit" sign, a stump of axe handles, a dig site
 * and a clerk's desk. Registered with outdoor/procs.ts; vertex-coloured, built things keep their outline.
 */
import * as THREE from 'three';
import { OUT, type Gen, combine, paint, registerOutdoorProcs, rockGeo, xf } from './procs';

const box = (w: number, h: number, d: number): THREE.BufferGeometry => new THREE.BoxGeometry(w, h, d).translate(0, h / 2, 0);
const cyl = (rt: number, rb: number, h: number, seg = 8): THREE.BufferGeometry => new THREE.CylinderGeometry(rt, rb, h, seg).translate(0, h / 2, 0);
const ball = (r: number, ws = 8, hs = 6): THREE.BufferGeometry => new THREE.SphereGeometry(r, ws, hs);
const WOOD = OUT.wood;
const DARK = OUT.woodDark;

/** A bucket (open top suggested by a dark disc). */
const bucket = (at: [number, number, number], color = 0x8a8f96): THREE.BufferGeometry[] => [
  paint(xf(cyl(0.26, 0.21, 0.42, 9), at), color, [0.75, 1.05]),
  paint(xf(new THREE.CylinderGeometry(0.235, 0.235, 0.02, 9), [at[0], at[1] + 0.41, at[2]]), 0x2a3a48, [0.9, 1]),
  paint(xf(new THREE.TorusGeometry(0.26, 0.015, 4, 10, Math.PI), [at[0], at[1] + 0.42, at[2]]), 0x4a4a50, [0.9, 1]),
];

/** Washing line: two posts, a sagging line, one striped sock and a lonely peg. */
const laundry: Gen = () => {
  const parts = [paint(cyl(0.05, 0.06, 1.8, 5), DARK), paint(xf(cyl(0.05, 0.06, 1.8, 5), [2.2, 0, 0]), DARK)];
  parts.push(paint(xf(new THREE.CylinderGeometry(0.012, 0.012, 2.2, 3).rotateZ(Math.PI / 2), [1.1, 1.7, 0]), 0xe8e0c8, [0.9, 1]));
  // The one sock (striped), hanging.
  parts.push(paint(xf(box(0.16, 0.42, 0.06), [0.75, 1.28, 0]), 0xd04a4a, [0.85, 1.05]));
  parts.push(paint(xf(box(0.16, 0.07, 0.065), [0.75, 1.46, 0]), 0xf2ecd8, [0.9, 1]));
  parts.push(paint(xf(box(0.26, 0.12, 0.065), [0.83, 1.28, 0]), 0xd04a4a, [0.85, 1.05]));
  // Empty pegs where the others were.
  for (const x of [1.25, 1.5, 1.75]) parts.push(paint(xf(box(0.04, 0.1, 0.03), [x, 1.66, 0]), 0xc8a878, [0.9, 1]));
  parts.push(...bucket([1.9, 0, 0.5], 0x9a7a52));
  return combine(parts);
};

/** A wicker basket with yarn balls and two needles. */
const yarnBasket: Gen = (rnd) => {
  const parts = [paint(cyl(0.42, 0.34, 0.36, 10), 0xb08850, [0.7, 1.05])];
  const yarn = [0xf07aa0, 0x7ac0f0, 0xf0d070, 0xa0e080];
  for (let i = 0; i < 4; i++) parts.push(paint(xf(ball(0.17 + rnd() * 0.04), [(rnd() - 0.5) * 0.4, 0.4 + rnd() * 0.08, (rnd() - 0.5) * 0.4]), yarn[i]!, [0.85, 1.1]));
  for (const s of [-1, 1]) parts.push(paint(xf(new THREE.CylinderGeometry(0.015, 0.015, 0.8, 4), [s * 0.08, 0.7, 0], [0, 0, s * 0.5]), 0xd8d0c0, [0.9, 1]));
  // A tiny half-knitted sock on the rim.
  parts.push(paint(xf(box(0.14, 0.3, 0.05), [0.36, 0.3, 0.1], [0, 0, -0.3]), 0xf07aa0, [0.85, 1.05]));
  return combine(parts);
};

/** A glass jar (pale green) with a slime blob inside, on a little crate, plus a notebook. */
const slimeJar: Gen = () => {
  const parts = [paint(box(0.7, 0.45, 0.5), WOOD, [0.7, 1.05])];
  parts.push(paint(xf(cyl(0.2, 0.2, 0.42, 10), [0, 0.45, 0]), 0xcfe8d8, [0.95, 1.1]));
  parts.push(paint(xf(ball(0.15).scale(1, 0.75, 1), [0, 0.6, 0]), 0x7fd36a, [0.85, 1.15]));
  parts.push(paint(xf(cyl(0.21, 0.21, 0.05, 10), [0, 0.87, 0]), 0x7a5a3a, [0.9, 1]));
  parts.push(paint(xf(box(0.28, 0.04, 0.2), [0.2, 0.45, 0.12], [0, 0.4, 0]), 0xf0e8d0, [0.9, 1]));
  return combine(parts);
};

/** Mister Stuffins' old post: the cross-pole with nobody on it, a hat on top, a little straw. */
const emptyPost: Gen = () => {
  const parts = [paint(cyl(0.07, 0.09, 2.5, 5), DARK, [0.7, 1])];
  parts.push(paint(xf(new THREE.CylinderGeometry(0.05, 0.05, 1.8, 5).rotateZ(Math.PI / 2), [0, 1.8, 0]), DARK, [0.9, 1]));
  parts.push(paint(xf(new THREE.CylinderGeometry(0.42, 0.44, 0.05, 9), [0, 2.52, 0], [0.2, 0, 0.15]), 0x5a4a30, [0.85, 1]));
  parts.push(paint(xf(new THREE.ConeGeometry(0.24, 0.36, 7), [0, 2.68, 0], [0.2, 0, 0.15]), 0x5a4a30, [0.85, 1.05]));
  for (let i = 0; i < 5; i++) parts.push(paint(xf(new THREE.ConeGeometry(0.06, 0.4, 4), [-0.3 + i * 0.15, 0.12, 0.2], [Math.PI / 2 - 0.2, i * 0.7, 0]), OUT.hay, [0.9, 1.1]));
  return combine(parts);
};

/** A small turnip patch: tilled rows, purple-white turnips with leaves, one gap (Gerald's). */
const turnips: Gen = (rnd) => {
  const parts = [paint(box(2.4, 0.08, 1.4), 0x5a4030, [0.8, 1])];
  for (let r = 0; r < 2; r++)
    for (let c = 0; c < 4; c++) {
      if (r === 0 && c === 2) continue;
      const x = -0.9 + c * 0.6;
      const z = -0.35 + r * 0.7;
      parts.push(paint(xf(ball(0.14).scale(1, 0.9, 1), [x, 0.14, z]), 0xd8b0e0, [0.85, 1.1]));
      for (let k = 0; k < 3; k++) parts.push(paint(xf(new THREE.ConeGeometry(0.05, 0.32, 3), [x + (rnd() - 0.5) * 0.08, 0.36, z], [(rnd() - 0.5) * 0.6, 0, (rnd() - 0.5) * 0.6]), OUT.leafMeadowLight, [0.85, 1.1]));
    }
  // Gerald's empty hole, with a tiny marker stick.
  parts.push(paint(xf(cyl(0.16, 0.12, 0.04, 8), [0.3, 0.07, -0.35]), 0x2a1a10, [0.9, 1]));
  parts.push(paint(xf(box(0.04, 0.4, 0.04), [0.3, 0.05, -0.12]), WOOD, [0.9, 1]));
  return combine(parts);
};

/** A spade stuck in a fresh mound, and a little headstone. */
const spadeMound: Gen = () => {
  const parts = [paint(xf(ball(0.8, 9, 5).scale(1.3, 0.35, 0.9), [0, 0, 0]), 0x6a4a30, [0.75, 1.05])];
  parts.push(paint(xf(cyl(0.035, 0.035, 1.3, 5), [0.2, 0.45, 0], [0, 0, 0.2]), WOOD, [0.85, 1]));
  parts.push(paint(xf(box(0.3, 0.38, 0.04), [0.12, 0.12, 0], [0, 0, 0.2]), 0x8a8f96, [0.8, 1.05]));
  parts.push(paint(xf(box(0.5, 0.75, 0.16), [-0.9, 0, -0.55], [0, 0.2, 0.05]), OUT.stone, [0.75, 1.05]));
  return combine(parts);
};

/** A fishing spot: a rod propped on a forked stick over the water side, a bucket, a stool. */
const fishingSpot: Gen = () => {
  const parts = [paint(xf(cyl(0.03, 0.03, 0.8, 4), [0.3, 0, 0.9], [0.25, 0, 0]), DARK, [0.85, 1])];
  parts.push(paint(xf(new THREE.CylinderGeometry(0.018, 0.03, 3.4, 4), [0.3, 1.3, 1.9], [-1.05, 0, 0]), 0x9a7448, [0.85, 1]));
  parts.push(paint(xf(new THREE.CylinderGeometry(0.006, 0.006, 1.4, 3), [0.3, 1.6, 3.4]), 0xe8e8e8, [0.95, 1]));
  parts.push(paint(xf(ball(0.06), [0.3, 0.9, 3.4]), 0xe04040, [0.9, 1.1]));
  parts.push(...bucket([-0.5, 0, 0.6]));
  parts.push(paint(xf(cyl(0.22, 0.22, 0.38, 7), [-0.9, 0, -0.2]), WOOD, [0.75, 1.05]));
  return combine(parts);
};

/** A soup pot on a little fire. */
const cookpot: Gen = () => {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 6; i++) parts.push(paint(xf(rockGeo(0.16, () => 0.5, 0.7), [Math.cos(i) * 0.5, 0, Math.sin(i) * 0.5]), OUT.stone, [0.75, 1.05]));
  for (let i = 0; i < 3; i++) parts.push(paint(xf(new THREE.CylinderGeometry(0.05, 0.05, 0.8, 4).rotateZ(Math.PI / 2), [0, 0.08, 0], [0, i * 1.05, 0]), DARK, [0.7, 0.95]));
  parts.push(paint(xf(ball(0.42, 10, 7).scale(1, 0.85, 1), [0, 0.5, 0]), 0x2e3036, [0.8, 1.1]));
  parts.push(paint(xf(cyl(0.34, 0.34, 0.03, 10), [0, 0.78, 0]), 0xc89a50, [0.9, 1.1]));
  parts.push(paint(xf(new THREE.CylinderGeometry(0.025, 0.025, 0.9, 4), [0.12, 1.05, 0], [0, 0, 0.35]), WOOD, [0.9, 1]));
  return combine(parts);
};

/** A tourist's suitcase with stickers and a folded map. */
const suitcase: Gen = () => {
  const parts = [paint(box(0.8, 0.55, 0.3), 0x9a5a3a, [0.75, 1.05])];
  parts.push(paint(xf(new THREE.TorusGeometry(0.12, 0.025, 4, 8, Math.PI), [0, 0.56, 0]), 0x3a2a20, [0.9, 1]));
  for (const [x, y, c] of [[-0.22, 0.32, 0xf0d060], [0.18, 0.18, 0x60b0f0], [0.2, 0.4, 0xf07090]] as const) parts.push(paint(xf(box(0.14, 0.1, 0.01), [x, y, 0.155]), c, [0.95, 1.05]));
  parts.push(paint(xf(box(0.5, 0.02, 0.36), [0.75, 0, 0.3], [0, 0.4, 0]), 0xf0e8c8, [0.9, 1]));
  return combine(parts);
};

/** An ore cart on a stub of rail with a lantern. */
const oreCart: Gen = (rnd) => {
  const parts = [paint(xf(box(1.2, 0.6, 0.8), [0, 0.3, 0]), 0x6a5a48, [0.7, 1.05])];
  for (const [x, z] of [[-0.4, -0.42], [0.4, -0.42], [-0.4, 0.42], [0.4, 0.42]] as const) parts.push(paint(xf(new THREE.CylinderGeometry(0.2, 0.2, 0.08, 8).rotateX(Math.PI / 2), [x, 0.2, z]), OUT.iron, [0.8, 1]));
  for (let i = 0; i < 5; i++) parts.push(paint(xf(rockGeo(0.2, rnd, 0.8), [(rnd() - 0.5) * 0.8, 0.95, (rnd() - 0.5) * 0.5]), 0x7a6a5a, [0.75, 1.1]));
  for (const z of [-0.42, 0.42]) parts.push(paint(xf(box(2.6, 0.06, 0.08), [0, 0, z]), OUT.iron, [0.85, 1]));
  parts.push(paint(xf(cyl(0.03, 0.03, 1.4, 4), [1.0, 0, -0.9]), DARK, [0.85, 1]));
  parts.push(paint(xf(box(0.2, 0.26, 0.2), [1.0, 1.25, -0.9]), 0xf0c060, [1.1, 1.3]));
  return combine(parts);
};

/** A goat (Margaret): stocky body, four legs, a head with horns and a bell. */
const goat: Gen = () => {
  const white = 0xece6da;
  const parts = [paint(xf(ball(0.42, 9, 6).scale(1.5, 0.95, 1), [0, 0.78, 0]), white, [0.8, 1.05])];
  for (const [x, z] of [[-0.42, -0.2], [0.42, -0.2], [-0.42, 0.2], [0.42, 0.2]] as const) parts.push(paint(xf(cyl(0.07, 0.06, 0.5, 5), [x, 0, z]), 0x8a7a68, [0.8, 1]));
  parts.push(paint(xf(ball(0.24, 8, 6).scale(1.2, 1, 0.9), [0.75, 1.1, 0]), white, [0.85, 1.05]));
  for (const z of [-0.1, 0.1]) parts.push(paint(xf(new THREE.ConeGeometry(0.05, 0.3, 4), [0.68, 1.4, z], [0, 0, 0.6]), 0x6a5a48, [0.85, 1]));
  parts.push(paint(xf(new THREE.ConeGeometry(0.06, 0.18, 4).rotateX(Math.PI), [0.95, 0.92, 0]), 0xd8d0c0, [0.85, 1]));
  parts.push(paint(xf(cyl(0.07, 0.09, 0.12, 6), [0.62, 0.78, 0]), 0xe0b040, [0.95, 1.15]));
  parts.push(paint(xf(new THREE.ConeGeometry(0.05, 0.25, 4), [-0.62, 0.95, 0], [0, 0, 1.0]), white, [0.85, 1]));
  return combine(parts);
};

/** A cluster of candles of every height on a crate (warm tips). */
const candles: Gen = (rnd) => {
  const parts = [paint(box(0.9, 0.5, 0.6), WOOD, [0.7, 1.05])];
  for (let i = 0; i < 7; i++) {
    const h = 0.18 + rnd() * 0.4;
    const x = (rnd() - 0.5) * 0.7;
    const z = (rnd() - 0.5) * 0.4;
    parts.push(paint(xf(cyl(0.05, 0.06, h, 6), [x, 0.5, z]), i % 3 === 0 ? 0xd8c890 : 0xf6efd8, [0.9, 1.1]));
    parts.push(paint(xf(new THREE.ConeGeometry(0.03, 0.08, 4), [x, 0.5 + h + 0.04, z]), 0xffc040, [1.2, 1.4]));
  }
  return combine(parts);
};

/** A hand-painted sign on a post (the "main bandit" sign) and a little flag. */
const banditSign: Gen = () => {
  const parts = [paint(cyl(0.05, 0.06, 1.7, 5), DARK, [0.75, 1])];
  parts.push(paint(xf(box(1.1, 0.5, 0.06), [0, 1.2, 0.06], [0, 0, 0.06]), 0xc8a878, [0.85, 1.05]));
  // Painted "letters": short dark bars and one heart.
  for (let i = 0; i < 4; i++) parts.push(paint(xf(box(0.14, 0.18, 0.02), [-0.36 + i * 0.22, 1.34 + (i % 2) * 0.02, 0.1]), 0x5a1a1a, [0.9, 1]));
  parts.push(paint(xf(box(0.7, 0.05, 0.02), [0, 1.24, 0.1]), 0x5a1a1a, [0.9, 1]));
  parts.push(paint(xf(new THREE.ConeGeometry(0.22, 0.4, 3).rotateZ(-Math.PI / 2), [0.22, 1.62, 0]), 0xb04040, [0.85, 1.05]));
  return combine(parts);
};

/** A stump with a heap of chewed axe handles. */
const handleStump: Gen = (rnd) => {
  const parts = [paint(cyl(0.45, 0.55, 0.55, 9), OUT.bark, [0.7, 1.05]), paint(xf(cyl(0.44, 0.44, 0.02, 9), [0, 0.55, 0]), 0xd8b880, [0.9, 1.05])];
  for (let i = 0; i < 7; i++) {
    const a = rnd() * Math.PI;
    parts.push(paint(xf(new THREE.CylinderGeometry(0.035, 0.04, 0.95, 4).rotateZ(Math.PI / 2), [0.9 + (rnd() - 0.5) * 0.5, 0.05 + i * 0.06, (rnd() - 0.5) * 0.6], [0, a, 0]), 0x9a7448, [0.85, 1.05]));
  }
  return combine(parts);
};

/** A dig site: a stone pile, little flags, a brush and a tray of "artifacts" (pebbles). */
const digSite: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 6; i++) parts.push(paint(xf(rockGeo(0.22 + rnd() * 0.15, rnd, 0.7), [(rnd() - 0.5) * 1.2, 0, (rnd() - 0.5) * 0.8]), 0xd8d2c4, [0.75, 1.05]));
  for (let i = 0; i < 4; i++) {
    const x = -0.8 + i * 0.5;
    parts.push(paint(xf(cyl(0.012, 0.012, 0.6, 3), [x, 0, 0.7]), 0x6a5a48, [0.9, 1]));
    parts.push(paint(xf(box(0.16, 0.1, 0.01), [x + 0.08, 0.52, 0.7]), i % 2 ? 0xf0d040 : 0xe05050, [0.95, 1.1]));
  }
  parts.push(paint(xf(box(0.6, 0.06, 0.4), [0.9, 0, -0.3]), WOOD, [0.85, 1]));
  for (let i = 0; i < 5; i++) parts.push(paint(xf(ball(0.05, 5, 4), [0.75 + (i % 3) * 0.12, 0.09, -0.4 + Math.floor(i / 3) * 0.15]), 0xa8a29a, [0.85, 1.1]));
  return combine(parts);
};

/** A clerk's desk: a tall paper stack, an ink pot, a stamp and a "take a number" stand. */
const clerkDesk: Gen = () => {
  const parts = [paint(xf(box(1.3, 0.08, 0.7), [0, 0.78, 0]), WOOD, [0.8, 1.05])];
  for (const [x, z] of [[-0.58, -0.28], [0.58, -0.28], [-0.58, 0.28], [0.58, 0.28]] as const) parts.push(paint(xf(box(0.08, 0.78, 0.08), [x, 0, z]), DARK, [0.8, 1]));
  for (let i = 0; i < 9; i++) parts.push(paint(xf(box(0.36, 0.05, 0.26), [-0.3 + (i % 2) * 0.02, 0.86 + i * 0.05, 0], [0, (i % 3) * 0.08, 0]), 0xf4efe0, [0.9, 1.05]));
  parts.push(paint(xf(cyl(0.06, 0.07, 0.1, 7), [0.25, 0.86, 0.12]), 0x2a2a40, [0.85, 1]));
  parts.push(paint(xf(box(0.12, 0.08, 0.08), [0.42, 0.86, -0.12]), 0xb04040, [0.9, 1.05]));
  parts.push(paint(xf(cyl(0.025, 0.025, 0.14, 5), [0.42, 0.94, -0.12]), DARK, [0.9, 1]));
  parts.push(paint(xf(cyl(0.03, 0.03, 1.2, 4), [0.95, 0, 0.3]), DARK, [0.85, 1]));
  parts.push(paint(xf(box(0.3, 0.3, 0.04), [0.95, 1.2, 0.3]), 0xf0e0b0, [0.9, 1.05]));
  parts.push(paint(xf(box(0.06, 0.18, 0.01), [0.95, 1.26, 0.325]), 0x2a2a40, [0.9, 1]));
  return combine(parts);
};

/** A lantern on a short pole (Brother Fennick's ghost-spotting kit, Sir Waverly's nightlight). */
const lanternPole: Gen = () => {
  const parts = [paint(cyl(0.04, 0.05, 1.6, 5), DARK, [0.8, 1]), paint(xf(new THREE.CylinderGeometry(0.02, 0.02, 0.4, 4).rotateZ(Math.PI / 2), [0.18, 1.58, 0]), DARK, [0.9, 1])];
  parts.push(paint(xf(box(0.2, 0.28, 0.2), [0.36, 1.2, 0]), 0xbfe0ff, [1.1, 1.3]));
  parts.push(paint(xf(new THREE.ConeGeometry(0.16, 0.12, 4), [0.36, 1.54, 0], [0, Math.PI / 4, 0]), OUT.iron, [0.85, 1]));
  return combine(parts);
};

registerOutdoorProcs({
  q_laundry: laundry,
  q_yarn_basket: yarnBasket,
  q_slime_jar: slimeJar,
  q_empty_post: emptyPost,
  q_turnips: turnips,
  q_spade_mound: spadeMound,
  q_fishing_spot: fishingSpot,
  q_cookpot: cookpot,
  q_suitcase: suitcase,
  q_ore_cart: oreCart,
  q_goat: goat,
  q_candles: candles,
  q_bandit_sign: banditSign,
  q_handle_stump: handleStump,
  q_dig_site: digSite,
  q_clerk_desk: clerkDesk,
  q_lantern_pole: lanternPole,
});

/** Every quest prop id (tests). */
export const QUEST_PROPS = ['q_laundry', 'q_yarn_basket', 'q_slime_jar', 'q_empty_post', 'q_turnips', 'q_spade_mound', 'q_fishing_spot', 'q_cookpot', 'q_suitcase', 'q_ore_cart', 'q_goat', 'q_candles', 'q_bandit_sign', 'q_handle_stump', 'q_dig_site', 'q_clerk_desk', 'q_lantern_pole'] as const;
